// Maps a real IAthleteProfile (from the store/API) to a ProfilePreview for PlayProfileView.
// Out-of-scope fields (OVR, verified, media, metrics) are static placeholders.
// `teams` is set to [] here but OVERRIDDEN with real memberships by
// useActiveAthletePreview / useOrganizationsPreview — keep that in mind when reading the return.

import { IAthleteProfile } from 'API/profile'
import type { ProfilePreview, SocialLinkPreview } from './profile-preview.mock'
import { computeAge, formatMonthYear, formatHeight } from 'utils/profile-format'

/** Build the social-links chip list from the stored handle fields (empty → omitted). */
export const buildSocialLinks = (profile: IAthleteProfile): SocialLinkPreview[] => {
  const links: SocialLinkPreview[] = []
  if (profile.instagram?.trim()) links.push({ platform: 'instagram', handle: profile.instagram.trim() })
  if (profile.youtube?.trim()) links.push({ platform: 'youtube', handle: profile.youtube.trim() })
  if (profile.twitterX?.trim()) links.push({ platform: 'twitterX', handle: profile.twitterX.trim() })
  if (profile.facebook?.trim()) links.push({ platform: 'facebook', handle: profile.facebook.trim() })
  return links
}

const formatDob = (dobIso: string | undefined): string | undefined => {
  if (!dobIso) return undefined
  const d = new Date(dobIso)
  if (isNaN(d.getTime())) return undefined
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`
}

// ---------------------------------------------------------------------------
// Main mapper
// ---------------------------------------------------------------------------

/**
 * Convert a stored IAthleteProfile into a ProfilePreview consumed by PlayProfileView.
 * OVR (85), verified (true), media/metrics remain static placeholders. `teams` is
 * overridden with real memberships by useActiveAthletePreview; `socialLinks` is
 * derived from the stored handle fields here.
 */
export const athleteProfileToPreview = (profile: IAthleteProfile): ProfilePreview => {
  const age = computeAge(profile.dob)

  const fullName =
    [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim() ||
    profile.fullName?.trim() ||
    undefined

  // positionLine: "CATCHER · RIGHT / SWITCH"
  const parts: string[] = []
  if (profile.position) parts.push(profile.position.toUpperCase())
  if (profile.throws || profile.bats) {
    const throwsBats = [profile.throws, profile.bats].filter(Boolean).join(' / ').toUpperCase()
    if (throwsBats) parts.push(throwsBats)
  }
  const positionLine = parts.length > 0 ? parts.join(' · ') : undefined

  const positionTags = profile.secondaryPositions ?? []

  // jersey overlay: "{age} - '{YY of graduationYear}"
  const yy = profile.graduationYear ? String(profile.graduationYear).slice(-2) : null
  const jersey =
    age !== null && yy !== null
      ? `${age} - '${yy}`
      : age !== null
      ? String(age)
      : undefined

  const classYear = profile.graduationYear ? String(profile.graduationYear) : undefined
  // classSub: "Sr · {age} yo" — 'Sr' is a hardcoded class-standing placeholder for now.
  const classSub = age !== null ? `Sr · ${age} yo` : 'Sr'

  const height =
    typeof profile.height === 'number' && profile.height > 0
      ? formatHeight(profile.height)
      : undefined
  const heightSub = height ? formatMonthYear(profile.heightUpdatedAt) : undefined

  const weight =
    typeof profile.weight === 'number' && profile.weight > 0
      ? `${profile.weight} lb`
      : undefined
  const weightSub = weight ? formatMonthYear(profile.weightUpdatedAt) : undefined

  const visibility: 'public' | 'private' =
    profile.profileVisibility === 'public' ? 'public' : 'private'

  return {
    // Static placeholders (out of scope this phase)
    ovr: 85,
    verified: true,
    // Overridden by useActiveAthletePreview with real memberships (useOrganizationsPreview).
    teams: [],
    // Real fields
    fullName,
    avatarUrl: profile.avatarUrl || undefined,
    positionLine,
    positionTags,
    visibility,
    jersey,
    classYear,
    classSub,
    height,
    heightSub,
    weight,
    weightSub,
    gender: profile.gender || undefined,
    dob: formatDob(profile.dob),
    level: profile.level || undefined,
    developmentFocus: [],
    metrics: profile.metrics ?? [],
    media: (profile.media ?? []).map((url, i) => ({ id: `${i}`, thumbUrl: url })),
    socialLinks: buildSocialLinks(profile),
  }
}
